/*
PURPOSE: Clickable user mention component for @mentions in comments

USE CASES:
- Render @mentions as clickable links with proper styling
- Navigate to user profiles when clicked
- Apply Coral brand styling to mentions
- Handle invalid mentions gracefully

DOMAIN BOUNDARIES:
- Responsible for rendering clickable user mentions only
- Does NOT detect mentions (handled by mentionProcessor utility)
- Does NOT store or manage mention state
- UI component focused on presentation and interaction

ASSUMPTIONS:
- Mentions have already been validated by mentionProcessor
- Links should navigate to user profile pages
- Invalid mentions should render as plain text
- Styling should match Coral brand colors
*/

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { MentionInfo } from '../../utils/mentionProcessor';

interface UserMentionProps {
  mentionInfo: MentionInfo;
  children: React.ReactNode;
  className?: string;
}

const UserMention: React.FC<UserMentionProps> = ({
  mentionInfo,
  children,
  className = ''
}) => {
  const navigate = useNavigate();

  // If mention is invalid, render as plain text
  if (!mentionInfo.isValid || !mentionInfo.userId) {
    return <span className={className}>{children}</span>;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(`/u/${mentionInfo.userId}`);
  };

  return (
    <span
      onClick={handleClick}
      className={`text-coral-pink hover:text-coral-orange transition-colors duration-200 cursor-pointer font-medium ${className}`}
      title={`View ${mentionInfo.displayName || mentionInfo.username}'s profile`}
      data-mention-user-id={mentionInfo.userId}
    >
      {children}
    </span>
  );
};

export default UserMention;
